// RigSentinel — Phase 1 Auth/Storage-Scoping.
//
// Explicit, opt-in, once-per-device-per-account migration of pre-existing
// UNSCOPED "rsc.*" data into the signed-in user's scoped namespace. This is
// the ONLY module in the app allowed to read the raw unscoped keys — every
// other storage module reads/writes exclusively through lib/accountScope.js.
//
// Non-destructive: unscoped originals are left in place (never deleted), so
// a second account on the same device can independently claim the same
// pre-existing data without a race.
//
// If the user dismisses the prompt, nothing is persisted permanently — the
// prompt reappears on the next sign-in (session-only dismissal), per product
// decision.

import { scoped, scopedSession } from "./accountScope";
import { getCurrentUid } from "./authState";

const CLAIM_FLAG_BASE = "rsc.legacyClaim.v1";
const SESSION_DISMISS_KEY = "rsc.legacyClaim.dismissedThisSession.v1"; // unscoped, tab-only

const LEGACY_KEYS_LOCAL = [
  "rsc.bha.v1", "rsc.dailyOps.v1", "rsc.ddr.drafts.v1", "rsc.drillstring.v1",
  "rsc.killsheets.legacy.v1", "rsc.killsheets.v1", "rsc.fluidDocs.v1",
  "rsc.mudPrograms.v1", "rsc.profiles.migration.v2", "rsc.profiles.migration.v2.log",
  "rsc.profiles.well.v1", "rsc.profiles.bha.v1", "rsc.profiles.casing.v1",
  "rsc.profiles.mud.v1", "rsc.profiles.pump.v1", "rsc.profiles.opstate.v1",
  "rsc.rce.migrationPrompt.v1", "rsc.overrides.v1", "rsc.project.v1",
  "rsc.engSession.v1", "rsc.snapshotSeq.v1", "rsc.reflibs.user.v1",
  "rsc.rigsheets.v1", "rsc.stackModeContexts.v1", "rsc.saved.v1", "rsc.favs.v1",
  "rsc.recent.v1", "rsc.settings.v1", "rsc.subseaProfile.v1", "rsc.wells.v1",
  "rsc.enterpriseTeam.v1", "rsc.flangeFavs.v1",
  "rsc.dailyMudReport.drafts.v1", "rsc.tripSheet.drafts.v1", "rsc.bitRunRecord.drafts.v1",
  "rsc.cementingReport.drafts.v1", "rsc.bopTestSheet.drafts.v1", "rsc.hseIncidentForm.drafts.v1",
  "rsc.toolboxTalk.drafts.v1", "rsc.preTourBriefing.drafts.v1", "rsc.preJobBriefing.drafts.v1",
  "rsc.jsaGenerator.drafts.v1", "rsc.downtimeReport.drafts.v1", "rsc.equipmentFailureReport.drafts.v1",
  "rsc.handoverNotes.drafts.v1", "rsc.dailyOperationsSummary.drafts.v1", "rsc.lotFitReport.drafts.v1",
  "rsc.subseaBopTestReport.drafts.v1", "rsc.surfaceBopTestReport.drafts.v1",
  "rsc.surfaceKickSheetDeviated.drafts.v1", "rsc.surfaceKickSheetHorizontal.drafts.v1",
  "rsc.surfaceKickSheetVertical.drafts.v1", "rsc.subseaKickSheetDeviated.drafts.v1",
  "rsc.subseaKickSheetHorizontal.drafts.v1", "rsc.subseaKickSheetVertical.drafts.v1",
];
const LEGACY_KEYS_SESSION = ["rsc.killsheet.draft.v1", "rsc.prefill.v1"];

function _rawLocal(base) { try { return localStorage.getItem(base); } catch { return null; } }
function _rawSession(base) { try { return sessionStorage.getItem(base); } catch { return null; } }

/** Is there unscoped legacy data on this device that the signed-in user
 *  has not yet claimed (and hasn't dismissed this session)? */
export function hasUnclaimedLegacyData() {
  if (!getCurrentUid()) return false;
  if (scoped.getItem(CLAIM_FLAG_BASE) === "claimed") return false;
  try { if (sessionStorage.getItem(SESSION_DISMISS_KEY) === "1") return false; } catch { /* noop */ }
  if (LEGACY_KEYS_LOCAL.some((k) => _rawLocal(k) != null)) return true;
  if (LEGACY_KEYS_SESSION.some((k) => _rawSession(k) != null)) return true;
  return false;
}

/** Copy every present unscoped key into the signed-in user's scoped
 *  namespace (never overwriting data already scoped) and mark claimed. */
export function claimLegacyData() {
  if (!getCurrentUid()) return { claimed: 0 };
  let claimed = 0;
  for (const base of LEGACY_KEYS_LOCAL) {
    const raw = _rawLocal(base);
    if (raw == null) continue;
    if (scoped.getItem(base) != null) continue; // never overwrite existing scoped data
    scoped.setItem(base, raw);
    claimed += 1;
  }
  for (const base of LEGACY_KEYS_SESSION) {
    const raw = _rawSession(base);
    if (raw == null) continue;
    if (scopedSession.getItem(base) != null) continue;
    scopedSession.setItem(base, raw);
    claimed += 1;
  }
  scoped.setItem(CLAIM_FLAG_BASE, "claimed");
  try { sessionStorage.removeItem(SESSION_DISMISS_KEY); } catch { /* noop */ }
  return { claimed };
}

/** "Not now" — reappears on the next sign-in (session-only, not persisted). */
export function skipLegacyClaimThisSession() {
  try { sessionStorage.setItem(SESSION_DISMISS_KEY, "1"); } catch { /* noop */ }
}
